import CloseIcon from "@mui/icons-material/Close";
import * as MUI from "@mui/material";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import CountrySelector from "../../../../components/form/CountrySelector";
import logger from "../../../../utils/logger";
import { parseClinicSpreadsheet } from "../utils/parseClinicSpreadsheet";
import SpreadsheetDropzone from "./SpreadsheetDropzone";

const DEFAULT_COUNTRY = "Croatia";

async function readRows(file) {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });
}

export default function ImportClinicsDialog({ open, onClose, onParsed }) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));
  const methods = useForm({ defaultValues: { country: DEFAULT_COUNTRY } });
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);

  function handleClose() {
    methods.reset({ country: DEFAULT_COUNTRY });
    setFile(null);
    setError(null);
    onClose();
  }

  async function handleParse({ country }) {
    setIsParsing(true);
    setError(null);
    try {
      const rows = parseClinicSpreadsheet(await readRows(file));
      onParsed(rows.map((row) => ({ ...row, country })));
      handleClose();
    } catch (err) {
      logger.error(err);
      setError(err.message || "SomethingWentWrong");
    } finally {
      setIsParsing(false);
    }
  }

  return (
    <MUI.Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.DialogTitle>
        <MUI.Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {t("ImportClinics")}
          <MUI.IconButton onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </MUI.IconButton>
        </MUI.Stack>
      </MUI.DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleParse)}>
          <MUI.DialogContent>
            <MUI.Stack spacing={2}>
              {error && <MUI.Alert severity="error">{t(error)}</MUI.Alert>}
              <SpreadsheetDropzone
                file={file}
                onFileChange={(nextFile) => {
                  setFile(nextFile);
                  setError(null);
                }}
              />
              <CountrySelector
                countryName="country"
                preselectedValue={DEFAULT_COUNTRY}
                minWidth={0}
              />
            </MUI.Stack>
          </MUI.DialogContent>
          <MUI.DialogActions sx={{ px: 3, pb: 3 }}>
            <MUI.Button
              type="submit"
              variant="contained"
              disabled={!file || isParsing}
            >
              {t("Confirm")}
            </MUI.Button>
          </MUI.DialogActions>
        </form>
      </FormProvider>
    </MUI.Dialog>
  );
}
